import { BlockProps } from '@/components/Block/Block';
import { useEffect, useRef, useState } from 'react';
import {
    mdiDelete,
    mdiFlower,
    mdiCat,
    mdiPig,
    mdiFan,
    mdiHelp,
    mdiRedo,
} from '@mdi/js';
import { DotWave } from '@uiball/loaders';
import { motion, useReducedMotion } from 'framer-motion';
import Icon from '@mdi/react';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';
import styles from './SketchAIBlock.module.sass';
import SoftPopup, {
    SoftPopupButton,
    SoftPopupDivider,
} from '@/components/SoftPopup/SoftPopup';
import { measure } from '@/components/Analytics/Analytics';

interface SketchClass {
    displayName: string;
    id: string;
    icon: string;
}

const SketchClasses: Array<SketchClass> = [
    {
        displayName: 'Bloem',
        id: 'flower',
        icon: mdiFlower,
    },
    {
        displayName: 'Kat',
        id: 'cat',
        icon: mdiCat,
    },
    {
        displayName: 'Varken',
        id: 'pig',
        icon: mdiPig,
    },
    {
        displayName: 'Windmolen',
        id: 'windmill',
        icon: mdiFan,
    },
];

interface SketchAIBlockData {
    default_class_id: string;
}

const SketchAIBlock = ({
    uuid,
    data,
    isEditable,
    isAnimated,
    onEdited,
    onFinished,
}: BlockProps) => {
    const sketchRnnRef = useRef<HTMLIFrameElement>(null);

    const [blockData, setBlockData] = useState<SketchAIBlockData>({
        default_class_id: data.default_class_id ?? SketchClasses[0].id,
    });

    const [sketchRnnFrameLoaded, setSketchRnnFrameLoaded] = useState(false);
    const [selectedSketchClassId, setSelectedSketchClassId] = useState(
        blockData.default_class_id,
    );
    const [isLoadingAIModel, setIsLoadingAIModel] = useState(false);
    const [isInteractionCompleted, setIsInteractionCompleted] = useState(false);

    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    const onSketchRnnFrameLoaded = () => setSketchRnnFrameLoaded(true);
    const onSketchRnnFrameMessage = (event: {
        data: { _hetklokhuisaistudio: boolean; type: string };
    }) => {
        if (event.data?._hetklokhuisaistudio) {
            switch (event.data.type) {
                case 'ai.drawing':
                    if (!isInteractionCompleted && onFinished) {
                        setIsInteractionCompleted(true);
                        setTimeout(onFinished, 5000);
                    }
                    break;
                case 'class.loading':
                    setIsLoadingAIModel(true);
                    break;
                case 'class.loaded':
                    setIsLoadingAIModel(false);
                    break;
            }
        }
    };

    useEffect(() => {
        if (sketchRnnRef.current) {
            sketchRnnRef.current.focus();
            sketchRnnRef.current.addEventListener(
                'load',
                onSketchRnnFrameLoaded,
            );
            window.addEventListener('message', onSketchRnnFrameMessage);
            sketchRnnRef.current.src = `/embedded/sketchrnn/index.html?autorestart=true`;
        }

        return () => {
            window.removeEventListener('message', onSketchRnnFrameMessage);
            if (sketchRnnRef.current)
                sketchRnnRef.current.removeEventListener(
                    'load',
                    onSketchRnnFrameLoaded,
                );
        };
    }, [sketchRnnRef]);

    useEffect(() => {
        if (sketchRnnRef.current && sketchRnnFrameLoaded) {
            sketchRnnRef.current.contentWindow?.postMessage({
                _hetklokhuisaistudio: true,
                type: 'class.change',
                classId: selectedSketchClassId,
            });
        }
    }, [selectedSketchClassId]);

    const deleteDrawing = () => {
        if (sketchRnnRef.current && sketchRnnFrameLoaded) {
            sketchRnnRef.current.contentWindow?.postMessage({
                _hetklokhuisaistudio: true,
                type: 'drawing.clear',
            });
        }
    };

    const showLoader = isLoadingAIModel || !sketchRnnFrameLoaded;

    return (
        <div
            className={`${styles.sketchaiblock} ${
                isEditable ? styles['sketchaiblock--editable'] : ''
            } ${isAnimated ? styles['sketchaiblock--animated'] : ''}`}
        >
            {isEditable && (
                <BlockEditorContainer className="bg-orange-100">
                    <select
                        className={styles['sketchaiblock--editable__select']}
                        value={blockData.default_class_id}
                        onChange={(event) =>
                            setBlockData({
                                ...blockData,
                                default_class_id: event.target.value,
                            })
                        }
                    >
                        {SketchClasses.map((sketchClass, index) => (
                            <option value={sketchClass.id} key={index}>
                                {sketchClass.displayName}
                            </option>
                        ))}
                    </select>
                </BlockEditorContainer>
            )}
            {!isEditable && (
                <>
                    <div className="w-full min-w-0 bg-secondary-text-base-aaa text-white">
                        <div className="flex p-2">
                            <ul
                                className="flex min-w-0 grow"
                                aria-label="Vormen"
                            >
                                {SketchClasses.map((sketchClass) => (
                                    <li key={sketchClass.id}>
                                        <SketchClassTab
                                            sketchClass={sketchClass}
                                            isActive={
                                                selectedSketchClassId ===
                                                sketchClass.id
                                            }
                                            onSelected={() =>
                                                setSelectedSketchClassId(
                                                    sketchClass.id,
                                                )
                                            }
                                        />
                                    </li>
                                ))}
                            </ul>
                            <SoftPopup
                                positioningClassName="top-12 right-52 origin-top-right"
                                panelClassName="w-64"
                                arrowPosition="top_end"
                                buttonProps={{
                                    onClick: () => {
                                        // test
                                    },
                                    title: 'Help of overslaan',
                                    className: `${
                                        styles[
                                            'sketchaiblock__controls__button'
                                        ]
                                    } text-secondary-text-lg-aaa hover:scale-105 hover:bg-secondary-800 ${
                                        isInteractionCompleted
                                            ? 'mr-1 md:mr-2'
                                            : 'translate-x-12'
                                    }`,
                                    'aria-label': 'Help of overslaan',
                                    children: (
                                        <Icon path={mdiHelp} size={1.2} />
                                    ),
                                }}
                            >
                                <p className="p-4">
                                    Het kan even duren tot de krabbel AI is
                                    geladen. De AI maakt een tekening af als je
                                    een begin hebt gemaakt. Je kan dit ook
                                    overslaan.
                                </p>
                                {!isInteractionCompleted && isAnimated && (
                                    <>
                                        <SoftPopupDivider />
                                        <SoftPopupButton
                                            onClick={() => {
                                                onFinished?.();
                                                setIsInteractionCompleted(true);
                                            }}
                                            icon={mdiRedo}
                                        >
                                            Tekenen overslaan
                                        </SoftPopupButton>
                                    </>
                                )}
                            </SoftPopup>

                            <button
                                onClick={deleteDrawing}
                                disabled={!isInteractionCompleted}
                                title="Wis tekening"
                                className={`${
                                    styles['sketchaiblock__controls__button']
                                } text-red-500 hover:scale-105 hover:bg-red-500 ${
                                    isInteractionCompleted
                                        ? ''
                                        : 'translate-x-16'
                                }`}
                                aria-label="Wis tekening"
                            >
                                <Icon path={mdiDelete} size={1.2} />
                            </button>
                        </div>
                    </div>
                    <iframe
                        ref={sketchRnnRef}
                        title="Teken AI"
                        className={styles['sketchaiblock__iframe']}
                    />
                    <div
                        className={`${styles['sketchaiblock__loader']} ${
                            !showLoader
                                ? styles['sketchaiblock__loader--hidden']
                                : ''
                        } `}
                    >
                        <DotWave color="white" />
                        {showLoader && <p className="sr-only">Laden</p>}
                    </div>
                    <p
                        className={`${styles['sketchaiblock__helptext']} ${
                            !showLoader
                                ? styles['sketchaiblock__helptext--bottom']
                                : ''
                        }`}
                    >
                        Teken samen met AI een{' '}
                        {SketchClasses.filter(
                            (info) => info.id === selectedSketchClassId,
                        )[0].displayName.toLocaleLowerCase()}
                    </p>
                    <button
                        className="sr-only focus:not-sr-only"
                        onClick={() => setIsInteractionCompleted(true)}
                    >
                        Tekenen overslaan
                    </button>
                </>
            )}
        </div>
    );
};

const SketchClassTab = ({
    sketchClass,
    isActive,
    onSelected,
}: {
    sketchClass: SketchClass;
    isActive: boolean;
    onSelected: () => void;
}) => {
    const reducedMotion = useReducedMotion();

    return (
        <button
            title={sketchClass.displayName}
            className={`relative rounded-lg px-3 md:px-4 h-12 text-left font-bold hover:bg-white/25 md:py-2 md:text-lg ${
                isActive ? 'scale-105' : 'scale-95 hover:scale-105'
            } transition-all`}
            onClick={() => {
                measure('draw_together_change_subject');
                measure(`draw_together_change_subject_${sketchClass.id}`);
                onSelected();
            }}
            aria-label={sketchClass.displayName}
            aria-pressed={isActive}
        >
            <Icon
                path={sketchClass.icon}
                size={1.2}
                className={`block transition-all duration-500${
                    isActive ? '-scale-x-100' : ''
                }`}
            />
            {isActive && (
                <div className="absolute inset-0 h-full w-full">
                    <motion.div
                        layoutId={
                            reducedMotion ? '' : 'sketch-class-tab-indicator'
                        }
                        layoutDependency={isActive}
                        className={`h-full w-full rounded-lg border-2 border-white`}
                    />
                </div>
            )}
        </button>
    );
};

export const preloadSketchAIBlock = () => {
    // Preload the model files
    const modelFilePaths = [
        '/embedded/sketchrnn/lib/ant.gen.js',
        '/embedded/sketchrnn/lib/models/flower.gen.json',
    ];

    modelFilePaths.forEach((modelFilePath) => {
        // Return if there is already a preload link
        if (document.head.querySelector(`link[href="${modelFilePath}"]`))
            return;

        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'script';
        preloadLink.href = modelFilePath;
        document.head.appendChild(preloadLink);
    });
};

export default SketchAIBlock;
